import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import api from '../../../../Services/api';
import { Alert, AlertTitle, Backdrop, Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { MdArrowBack, MdFileDownload, MdForward, MdCheckCircle, MdCancel } from 'react-icons/md';
import { ScreenGreeting } from '../../../../Components/Molecules';
import {
    Link as RouterLink
} from 'react-router-dom'

import {
    format
} from 'date-fns';
import { useToast } from '../../../../Hooks/toast';

function toCurrency(val = 0) {
    return Intl.NumberFormat('pt-BR', { currency: 'BRL', style: 'currency' }).format(val);
}

const PayrollDetail = () => {

    const [transfer, setTransfer] = useState({
        items: [] as PayrollPayment[],
        totalRows: 0 as number
    })

    const [filter, setFilter] = useState({
        page: 1,
        rowsPerPage: 5
    });

    const toast = useToast();

    const history = useHistory();

    const [payroll, setPayroll] = useState<PayrollBatch>();

    const { id } = useParams<{ id: string }>();

    const getTransfers = async () => {
        const params = { 
            page: filter.page,
            pagesize: filter.rowsPerPage
        }
        const { data: { items, row_count } } = await api.get(`payroll/rows/${id}`, { params });

        setTransfer({
            items,
            totalRows: row_count
        });
    }

    const getPayroll = async() => {
        const res = await api.get(`payroll/${id}`);
        setPayroll(res.data);
    }

    const reviewBatch = async (isAccepted = false) => {
        await api.post(`payroll/review/${id}/${isAccepted}`);

        toast.addToast({
            title: 'Sucesso',
            type: 'success',
            description: `O arquivo foi ${isAccepted ? 'aprovado' : 'rejeitado'} com sucesso.`
        });

        history.push('.');
    }

    useEffect(() => {
        getTransfers();
    }, [id, filter]);

    useEffect(() => {
        getPayroll();
    }, [id])

    if(!payroll) return null;

    return (
        <>
            <ScreenGreeting
                title={
                    <>
                        <IconButton sx={{ mr: 2 }} component={RouterLink} to={'.'} >
                            <MdArrowBack/>
                        </IconButton>
                        Detalhes do arquivo: {payroll.id} - {payroll.fileName}
                    </>
                }
                subTitle="Confira as transações dentro do arquivo enviado."
            />
            {
                payroll.status === 'Analyze' && 
                (
                    <Alert variant="standard" severity="info">
                        <AlertTitle>Aprovação necessária</AlertTitle>
                        <Typography variant="body2">
                            Este arquivo de folha de pagamento está aguardando revisão. Ao <strong>aprovar</strong>, as transferências serão autorizadas para processamento conforme as datas informadas. Ao <strong>rejeitar</strong>, o lote será cancelado e não seguirá para pagamento. Revise valores, destinatários e datas antes de decidir.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Observação: dependendo do estágio de processamento, esta ação pode ser irreversível.
                        </Typography>
                        <Box sx={{ mt: 3, gap: 2, display: 'flex', ml: -1 }}>
                            <Button
                                variant='text'
                                color='success'
                                startIcon={<MdCheckCircle size={18} />}
                                onClick={() => reviewBatch(true)}
                            >
                                Aprovar
                            </Button>
                            <Button
                                variant='text'
                                color='error'
                                startIcon={<MdCancel size={18} />}
                                onClick={() => reviewBatch(false)}
                            >
                                Rejeitar
                            </Button>
                        </Box>
                    </Alert>
                )
            }
            <Box sx={{ mt: 4 }}>
                <Typography variant='h5' mb={1}>Transferências</Typography>
                <TableContainer component={Paper} sx={{ whiteSpace: 'nowrap', overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={2}>Nome</TableCell>
                                <TableCell rowSpan={2}>Documento</TableCell>
                                <TableCell rowSpan={2}>Valor</TableCell>
                                <TableCell rowSpan={2}>Mensagem</TableCell>
                                <TableCell rowSpan={2}>Mensagem Validação</TableCell>
                                <TableCell align="center" colSpan={3} sx={{ py: 0.5 }}>Data</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ py: 0.5 }}>Agendamento</TableCell>
                                <TableCell sx={{ py: 0.5 }}>Pagamento</TableCell>
                                <TableCell sx={{ py: 0.5 }}>Criado em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                !transfer.items.length && (
                                    <TableRow>
                                        <TableCell colSpan={999}>
                                            <Typography align='center'>Não há pagamentos para serem exibidos.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {
                                transfer.items.map(transfer => (
                                    <TableRow key={transfer.id}>
                                        <TableCell>{transfer.name}</TableCell>
                                        <TableCell>{transfer.document}</TableCell>
                                        <TableCell>{toCurrency(transfer.amount)}</TableCell>
                                        <TableCell>{transfer.message}</TableCell>
                                        <TableCell>{transfer.messageValidation}</TableCell>
                                        <TableCell>{transfer.dateToPay && format(new Date(transfer.dateToPay), 'dd/MM/yyyy')}</TableCell>
                                        <TableCell>{transfer.datePayment && format(new Date(transfer.datePayment), 'dd/MM/yyyy')}</TableCell>
                                        <TableCell>{format(new Date(transfer.dateCreated), 'dd/MM/yyyy')}</TableCell>
                                    </TableRow>
                                ))
                            }

                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        onRowsPerPageChange={e => setFilter(oldPagination => ({ ...oldPagination, rowsPerPage: +e.target.value, page: 1  }))}
                        count={transfer.totalRows}
                        rowsPerPage={filter.rowsPerPage}
                        page={filter.page - 1}
                        onPageChange={(ev, page) => setFilter(oldPagination => ({ ...oldPagination, page: page + 1 }))}
                    />
                </TableContainer>
            </Box>            
        </>
    );
}

export default PayrollDetail;